import type { ComponentPropsWithRef, ReactNode } from 'react';
import { Popover } from '@base-ui/react/popover';
import { ChevronDown, MoreHorizontal } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import type { ButtonType } from '../../lib/buttonType.ts';
import { SectionLabel } from '../section-label/section-label.tsx';
import { Switch } from '../switch/switch.tsx';

export type HeroControlRowProps = Omit<ComponentPropsWithRef<'div'>, 'style'>;

/*
 * The row of settings under the hero bar — on a meeting: its gesprekstype,
 * what Wundo may do here, and what Wundo reads along.
 *
 * Under the bar rather than on it, and quiet where the bar is loud. These are
 * not actions: each one says what is true right now and opens the way to change
 * it, which is a different promise from a button on a banner. Keeping them off
 * the navy is also what lets them wear the page's own ink instead of a
 * white-alpha ramp that only works on one background.
 *
 * It wraps rather than shrinks: a control that says "Gesprekstype · 1:1" and is
 * squeezed to "Gespr…" says nothing at all.
 */
export function HeroControlRow({ className, ...props }: HeroControlRowProps) {
  return (
    <div
      data-slot='hero-control-row'
      className={cn('flex flex-wrap items-center gap-2.5', className)}
      {...props}
    />
  );
}

/*
 * One control's look: a soft pill carrying a picture, an optional name for what
 * it sets, and — always — the value it is set to.
 *
 * Under `mobile` the name goes off screen and the value stays: which setting
 * this is can be read from the icon and from the panel it opens, but a value
 * nobody can see is a control nobody can read. Off screen rather than gone —
 * the control keeps the same name whatever the screen is doing.
 */
const heroControlClasses = [
  'inline-flex items-center gap-1.75 rounded-full border border-transparent bg-fill py-2 pr-3.5 pl-3',
  'text-sm font-medium text-ink transition-colors',
  '[&>svg:first-child]:size-3.5 [&>svg:first-child]:shrink-0 [&>svg:first-child]:text-ink-muted',
];

const heroControlInteractive = [
  'cursor-pointer hover:border-line hover:bg-fill-faint',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
  'data-popup-open:border-line data-popup-open:bg-fill-faint',
];

export interface HeroControlProps extends Omit<
  ComponentPropsWithRef<'button'>,
  'style' | 'type'
> {
  type?: ButtonType;
  /** The picture in front, sized and coloured by the pill. */
  icon: ReactNode;
  /** What this control sets ("Gesprekstype"). Left out where the value says it. */
  label?: string;
  /** Whether pressing it opens something. */
  chevron?: boolean;
}

/**
 * A control under the hero bar. On its own it is a button; handed to a menu or
 * a popover through `render`, it is that trigger.
 */
export function HeroControl({
  className,
  icon,
  label,
  chevron = false,
  children,
  type = 'button',
  ...props
}: HeroControlProps) {
  return (
    <button
      data-slot='hero-control'
      type={type}
      className={cn(heroControlClasses, heroControlInteractive, className)}
      {...props}
    >
      {icon}
      {label !== undefined && (
        <>
          <span className='whitespace-nowrap text-ink-muted max-mobile:sr-only'>
            {label}
          </span>
          <span aria-hidden='true' className='text-ink-muted max-mobile:hidden'>
            ·
          </span>
        </>
      )}
      <span className='font-semibold whitespace-nowrap'>{children}</span>
      {chevron && (
        <ChevronDown aria-hidden='true' className='size-3 shrink-0 text-ink-muted' />
      )}
    </button>
  );
}

export interface HeroControlOverflowProps extends Omit<
  ComponentPropsWithRef<'button'>,
  'style' | 'children' | 'type'
> {
  type?: ButtonType;
  /** What the menu behind it holds, since the "…" itself names nothing. */
  'aria-label': string;
}

/**
 * The last control on the row: everything else there is to do here, folded
 * behind a "…". A menu trigger, handed to the menu through `render`.
 *
 * It wears the same pill as the settings beside it rather than a shape of its
 * own — the row already reads as one thing, and a second look at the end of it
 * would read as a second kind of thing. Round and icon-only because what it
 * holds has no single name; the names are inside.
 *
 * It stretches to the row's height instead of naming one: a "…" is shorter than
 * a line of text, and a pill that sits 4px low is the only thing anybody would
 * see in the row.
 */
export function HeroControlOverflow({
  className,
  type = 'button',
  ...props
}: HeroControlOverflowProps) {
  return (
    <button
      data-slot='hero-control-overflow'
      type={type}
      className={cn(
        heroControlClasses,
        heroControlInteractive,
        'self-stretch justify-center px-2.5',
        className,
      )}
      {...props}
    >
      <MoreHorizontal aria-hidden='true' />
    </button>
  );
}

export interface HeroControlStaticProps extends Omit<
  ComponentPropsWithRef<'span'>,
  'style'
> {
  icon: ReactNode;
  label?: string;
}

/**
 * The same control with nothing behind it — a setting that was decided for this
 * object and is not the user's to change (the gesprekstype of a team meeting).
 *
 * Same box, so the row keeps one height and one rhythm whether or not every
 * control on it can be opened. A disabled button would say the same thing
 * wrongly: this is not a control that is unavailable, it is a fact.
 */
export function HeroControlStatic({
  className,
  icon,
  label,
  children,
  ...props
}: HeroControlStaticProps) {
  return (
    <span
      data-slot='hero-control-static'
      className={cn(heroControlClasses, className)}
      {...props}
    >
      {icon}
      {label !== undefined && (
        <>
          <span className='whitespace-nowrap text-ink-muted max-mobile:sr-only'>
            {label}
          </span>
          <span aria-hidden='true' className='text-ink-muted max-mobile:hidden'>
            ·
          </span>
        </>
      )}
      <span className='font-semibold whitespace-nowrap'>{children}</span>
    </span>
  );
}

export interface HeroControlPanelProps {
  icon: ReactNode;
  label?: string;
  /** What the pill says: the state this control is in right now. */
  value: string;
  /** The panel's own heading, which also names it for a screen reader. */
  title: string;
  /** One sentence under the heading, where the title needs one. */
  description?: string;
  /**
   * Whether the panel stands open, for a caller that opens it from elsewhere on
   * the page — the chat drawer pointing somebody at the documents. Left out, the
   * panel answers to its own trigger and nothing else.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

/**
 * A control whose panel is a small page of its own — a switch with an
 * explanation, a list of what Wundo reads along.
 *
 * A popover rather than a menu: what is inside is read and operated, not picked
 * from. Base UI keeps the focus and the escape key honest; everything this adds
 * is the heading, so every such panel says what it is about before it says
 * anything else.
 */
export function HeroControlPanel({
  icon,
  label,
  value,
  title,
  description,
  open,
  onOpenChange,
  children,
}: HeroControlPanelProps) {
  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger render={<HeroControl icon={icon} label={label} chevron />}>
        {value}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner side='bottom' align='start' sideOffset={8} className='z-50'>
          <Popover.Popup
            data-slot='hero-control-panel'
            aria-label={title}
            className='w-85 rounded-md border border-line bg-surface px-4 pt-3.5 pb-3 shadow-menu outline-none animate-pop'
          >
            <SectionLabel>{title}</SectionLabel>
            {description !== undefined && (
              <p className='mt-1.5 text-sm text-ink-muted'>{description}</p>
            )}
            {children}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

export interface HeroControlSwitchProps {
  /** What is being switched, and the switch's own name. */
  label: string;
  /** What happens when it is on. One sentence, always shown. */
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}

/**
 * A switch inside a control's panel, with the sentence that says what it does.
 *
 * A real switch here, where the row has the width for a label beside it — the
 * panel is where a setting is explained, so it can afford to be a switch rather
 * than a pill that has to double as its own label.
 */
export function HeroControlSwitch({
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}: HeroControlSwitchProps) {
  return (
    <div data-slot='hero-control-switch' className='flex items-start gap-4 py-2.5'>
      <div className='min-w-0 flex-1'>
        <p className='text-md font-semibold text-ink'>{label}</p>
        <p className='mt-0.5 text-md text-ink-muted'>{description}</p>
      </div>
      <Switch
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </div>
  );
}

export type HeroControlNoteProps = Omit<ComponentPropsWithRef<'p'>, 'style'>;

/* The line at the foot of a panel: where this setting came from, how far it reaches. */
export function HeroControlNote({ className, ...props }: HeroControlNoteProps) {
  return (
    <p
      data-slot='hero-control-note'
      className={cn(
        'mt-2.5 border-t border-fill pt-2.75 text-sm text-ink-muted',
        className,
      )}
      {...props}
    />
  );
}

export interface HeroControlItemProps {
  /**
   * What this is — the name a reader would recognise it by. A node rather than
   * a string, because on some rows the name is the way in: a document's name is
   * the link the file itself comes back out of.
   */
  name: ReactNode;
  /** The line under it: when it is from, what state it is in. */
  meta: string;
  /** The way to open it, when there is one. */
  action?: ReactNode;
}

/** One thing a panel lists — a previous conversation Wundo reads along. */
export function HeroControlItem({ name, meta, action }: HeroControlItemProps) {
  return (
    <div
      data-slot='hero-control-item'
      className='flex items-center gap-3 border-t border-fill py-2.5 first:border-t-0'
    >
      <div className='min-w-0 flex-1'>
        <p className='truncate text-base font-semibold text-ink'>{name}</p>
        <p className='truncate text-sm text-ink-muted'>{meta}</p>
      </div>
      {action}
    </div>
  );
}

export interface HeroControlSectionProps {
  title: string;
  children: ReactNode;
}

/** A titled group inside a panel, for a panel that lists more than one kind of thing. */
export function HeroControlSection({ title, children }: HeroControlSectionProps) {
  return (
    <div data-slot='hero-control-section' className='mt-3.5'>
      <SectionLabel>{title}</SectionLabel>
      <div className='mt-1'>{children}</div>
    </div>
  );
}
